import type { RouteRecordRaw } from 'vue-router'
import type { Router } from 'vue-router'
import ToastEventBus from 'primevue/toasteventbus'
import { queryClient } from '@/api/queryClient'
import { getCurrentUser, getGetCurrentUserQueryKey } from '@/api/generated'
import type { CurrentUserDto } from '@/api/models/currentUserDto'
import { PlatformRole } from '@/api/models/platformRole'
import {
  REVIEWS_PATH,
  SIGN_OFF_PATH,
  CERTIFICATION_PATH,
  ANNUAL_REVIEWS_PATH,
  ANNUAL_SIGN_OFF_PATH,
  COMPLIANCE_REGULATORY_REFERENCES_PATH,
} from '@/pages/compliance-reviews/routes'
import {
  REGULATORY_REFERENCES_PATH,
  REGULATORY_REFERENCES_COMPLETED_PATH,
  REGULATORY_REFERENCES_NOT_REQUIRED_PATH,
} from '@/pages/regulatory-references/routes'
import {
  ANNUAL_CERTIFICATION_PATH,
  ANNUAL_CERTIFICATION_NOT_REQUIRED_PATH,
  ANNUAL_CERTIFICATION_COMPLETED_PATH,
} from '@/pages/annual-certification/routes'
import { LINE_MANAGER_ANNUAL_PATH } from '@/pages/line-manager-reviews/routes'

declare module 'vue-router' {
  interface RouteMeta {
    requiresRoles?: PlatformRole[]
  }
}

export const routes: RouteRecordRaw[] = [
  {
    path: '/sign-in',
    name: 'sign-in',
    component: () => import('@/pages/sign-in/index.vue'),
  },
  {
    path: '/sign-up',
    name: 'sign-up',
    component: () => import('@/pages/sign-up/index.vue'),
  },
  {
    path: '/invite-accepted',
    name: 'invite-accepted',
    component: () => import('@/pages/invite-accepted/index.vue'),
  },
  {
    path: '/',
    component: () => import('@/layouts/DefaultLayout.vue'),
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/pages/dashboard/index.vue'),
      },
      {
        path: 'dev',
        name: 'dev',
        component: () => import('@/pages/dev/index.vue'),
      },
      {
        path: 'dev/components',
        name: 'dev-components',
        component: () => import('@/pages/dev/component-showcase/index.vue'),
      },
      {
        path: 'dev/forms',
        name: 'dev-forms',
        component: () => import('@/pages/dev/forms/index.vue'),
      },
      {
        path: 'dev/vueuse',
        name: 'dev-vueuse',
        component: () => import('@/pages/dev/vueuse/index.vue'),
      },
      {
        path: 'dev/typography',
        name: 'dev-typography',
        component: () => import('@/pages/dev/typography/index.vue'),
      },
      {
        path: 'dev/people-seed',
        name: 'dev-people-seed',
        component: () => import('@/pages/dev/people-seed/index.vue'),
      },
      {
        path: 'people',
        name: 'people',
        component: () => import('@/pages/people/index.vue'),
        meta: { requiresRoles: [PlatformRole.HR] },
      },
      {
        path: 'people/:id',
        name: 'people-detail',
        component: () => import('@/pages/people/[id]/index.vue'),
        meta: { requiresRoles: [PlatformRole.HR] },
      },
      {
        path: 'hr-reviews',
        name: 'hr-reviews',
        component: () => import('@/pages/hr-reviews/index.vue'),
        meta: { requiresRoles: [PlatformRole.HR] },
      },
      {
        // HR Reviews → Annual Process (SMCR-336). Declared ahead of the
        // `:processId` record below for readability; the static segment
        // outranks the param either way, and a processId is a Guid, so the two
        // cannot collide.
        path: 'hr-reviews/annual',
        name: 'hr-reviews-annual',
        component: () => import('@/pages/hr-reviews/annual/index.vue'),
        meta: { requiresRoles: [PlatformRole.HR] },
      },
      {
        // Path must stay exactly `hr-reviews/annual/{processId}` — SMCR-327
        // hard-codes this as the HR Review Requested notification's deep link.
        // Declared ahead of the generic `hr-reviews/:processId` below, same
        // static-beats-param ordering as `hr-reviews/annual` itself.
        path: 'hr-reviews/annual/:processId',
        name: 'hr-reviews-annual-detail',
        component: () => import('@/pages/hr-reviews/annual/[processId]/index.vue'),
        meta: { requiresRoles: [PlatformRole.HR] },
      },
      {
        // HR Reviews → Regulatory References (SMCR-314). Same static-beats-param
        // ordering note as `hr-reviews/annual` above.
        path: 'hr-reviews/regulatory-references',
        name: 'hr-regulatory-references',
        component: () => import('@/pages/hr-reviews/regulatory-references/index.vue'),
        meta: { requiresRoles: [PlatformRole.HR] },
      },
      {
        path: 'hr-reviews/regulatory-references/:processId',
        name: 'hr-regulatory-reference-detail',
        component: () => import('@/pages/hr-reviews/regulatory-references/[processId]/index.vue'),
        meta: { requiresRoles: [PlatformRole.HR] },
      },
      {
        path: 'hr-reviews/:processId',
        name: 'hr-review-detail',
        component: () => import('@/pages/hr-reviews/[processId]/index.vue'),
        meta: { requiresRoles: [PlatformRole.HR] },
      },
      {
        path: 'users',
        name: 'users',
        component: () => import('@/pages/users/index.vue'),
        meta: { requiresRoles: [PlatformRole.CompanyAdmin] },
      },
      {
        path: 'users/:id',
        name: 'users-detail',
        component: () => import('@/pages/users/[id]/index.vue'),
        meta: { requiresRoles: [PlatformRole.CompanyAdmin] },
      },
      {
        // The three tabs are children rather than three sibling records each
        // rendering their own `ComplianceReviewsHeader`. That was the previous
        // shape, and it meant switching tabs unmounted the whole page —
        // header included — and remounted a fresh one, re-subscribing all
        // three of the header's count-badge queries every time. Nesting them
        // means `compliance-reviews/index.vue` (this component) and its
        // `ComplianceReviewsHeader` mount once per section visit; only the
        // active child renders through its own `<router-view />`.
        //
        // `meta` here is inherited by all three children (Vue Router merges
        // matched records' `meta` into one object), so it is not repeated on
        // each of them.
        path: REVIEWS_PATH,
        component: () => import('@/pages/compliance-reviews/index.vue'),
        meta: { requiresRoles: [PlatformRole.Compliance] },
        children: [
          {
            // The default child — `''` matches the parent's own path exactly,
            // which is the standard Vue Router idiom for "this tab has no
            // extra path segment." No constant needed: it is defined as
            // "whatever `REVIEWS_PATH` already is."
            path: '',
            name: 'compliance-reviews',
            component: () => import('@/pages/compliance-reviews/reviews/index.vue'),
          },
          {
            // Absolute, and imported rather than retyped, so the tab's path,
            // its active-state check and its list table's route-prefix cannot
            // drift apart. A child record accepts an absolute path.
            path: SIGN_OFF_PATH,
            name: 'compliance-sign-off',
            component: () => import('@/pages/compliance-reviews/sign-off/index.vue'),
          },
          {
            path: CERTIFICATION_PATH,
            name: 'compliance-certification',
            component: () => import('@/pages/compliance-reviews/certification/index.vue'),
          },
        ],
      },
      {
        // Compliance Reviews → Annual Process (SMCR-333: shell + Pending
        // Reviews tab; SMCR-334: Sign Off tab's own list/review page).
        //
        // Deliberately a sibling of the `REVIEWS_PATH` record above rather than
        // a fourth child of it: that record's component renders
        // `ComplianceReviewsHeader` and its three New Starter tabs, and annual
        // has its own two sub-tabs. Static segments outrank dynamic ones in Vue
        // Router, so this is matched ahead of `compliance-reviews/:processId`
        // below regardless of declaration order — it is declared first anyway so
        // the file reads in the order it resolves.
        path: ANNUAL_REVIEWS_PATH,
        component: () => import('@/pages/compliance-reviews/annual/index.vue'),
        meta: { requiresRoles: [PlatformRole.Compliance] },
        children: [
          {
            path: '',
            name: 'compliance-reviews-annual',
            component: () => import('@/pages/compliance-reviews/annual/reviews/index.vue'),
          },
          {
            path: ANNUAL_SIGN_OFF_PATH,
            name: 'compliance-reviews-annual-sign-off',
            component: () => import('@/pages/compliance-reviews/annual/sign-off/index.vue'),
          },
        ],
      },
      {
        // Compliance Reviews → Regulatory References (SMCR-315). Same
        // static-beats-param ordering note as `hr-reviews/regulatory-references`.
        path: COMPLIANCE_REGULATORY_REFERENCES_PATH,
        name: 'compliance-regulatory-references',
        component: () => import('@/pages/compliance-reviews/regulatory-references/index.vue'),
        meta: { requiresRoles: [PlatformRole.Compliance] },
      },
      {
        path: `${COMPLIANCE_REGULATORY_REFERENCES_PATH}/:processId`,
        name: 'compliance-regulatory-reference-detail',
        component: () =>
          import('@/pages/compliance-reviews/regulatory-references/[processId]/index.vue'),
        meta: { requiresRoles: [PlatformRole.Compliance] },
      },
      {
        path: 'compliance-reviews/:processId',
        name: 'compliance-review-detail',
        component: () => import('@/pages/compliance-reviews/[processId]/index.vue'),
        meta: { requiresRoles: [PlatformRole.Compliance] },
      },
      {
        path: `${ANNUAL_REVIEWS_PATH}/:processId`,
        name: 'compliance-reviews-annual-detail',
        component: () => import('@/pages/compliance-reviews/annual/[processId]/index.vue'),
        meta: { requiresRoles: [PlatformRole.Compliance] },
      },
      {
        // Static segment `sign-off` outranks the `:processId` param above it in
        // Vue Router's matching order, the same reasoning as the
        // `ANNUAL_REVIEWS_PATH` block's own comment — so this is unambiguous
        // against `compliance-reviews-annual-detail` regardless of declaration
        // order. Path matches SMCR-327's hard-coded deep link
        // `compliance-reviews/annual/sign-off/{processId}` exactly.
        path: `${ANNUAL_SIGN_OFF_PATH}/:processId`,
        name: 'compliance-reviews-annual-sign-off-detail',
        component: () => import('@/pages/compliance-reviews/annual/sign-off/[processId]/index.vue'),
        meta: { requiresRoles: [PlatformRole.Compliance] },
      },
      {
        path: 'compliance-reviews/sign-off/:processId',
        name: 'compliance-sign-off-detail',
        component: () => import('@/pages/compliance-reviews/sign-off/[processId]/index.vue'),
        meta: { requiresRoles: [PlatformRole.Compliance] },
      },
      {
        path: 'compliance-reviews/certification/:processId',
        name: 'compliance-certification-detail',
        component: () => import('@/pages/compliance-reviews/certification/[processId]/index.vue'),
        meta: { requiresRoles: [PlatformRole.Compliance] },
      },
      {
        path: 'new-starters',
        name: 'new-starters',
        component: () => import('@/pages/new-starters/index.vue'),
        meta: { requiresRoles: [PlatformRole.Compliance] },
      },
      {
        // Three tabs as children of one parent record, same shape as the
        // `compliance-reviews` block above — the shell (header + tab bar)
        // mounts once per section visit, not once per tab switch.
        path: REGULATORY_REFERENCES_PATH,
        component: () => import('@/pages/regulatory-references/index.vue'),
        meta: { requiresRoles: [PlatformRole.Compliance] },
        children: [
          {
            path: '',
            name: 'regulatory-references',
            component: () => import('@/pages/regulatory-references/pending/index.vue'),
          },
          {
            path: REGULATORY_REFERENCES_COMPLETED_PATH,
            name: 'regulatory-references-completed',
            component: () => import('@/pages/regulatory-references/completed/index.vue'),
          },
          {
            path: REGULATORY_REFERENCES_NOT_REQUIRED_PATH,
            name: 'regulatory-references-not-required',
            component: () => import('@/pages/regulatory-references/not-required/index.vue'),
          },
        ],
      },
      // --- Annual Process placeholder routes (SMCR-328) ---
      //
      // SMCR-328 adds the Annual Process sidebar entries before the screens
      // behind them exist. These keep those links landing somewhere honest
      // instead of falling through to the catch-all not-found route. Each is
      // replaced by its screen ticket, which repoints `component` at the real
      // page; `pages/coming-soon/index.vue` is deleted with the last of them.
      //
      // Roles mirror the sidebar `group` each link lives under, so a route is
      // reachable by exactly the people who can see it.
      {
        // Initiate Process → Annual Process (SMCR-330). One child for now
        // (Pending, default) — nested the same way `REVIEWS_PATH` nests its
        // three tabs, so `SMCR-358`/`SMCR-354` can add Not Required/Completed
        // as siblings later without a route restructure.
        path: ANNUAL_CERTIFICATION_PATH,
        component: () => import('@/pages/annual-certification/index.vue'),
        meta: { requiresRoles: [PlatformRole.Compliance] },
        children: [
          {
            path: '',
            name: 'annual-certification',
            component: () => import('@/pages/annual-certification/pending/index.vue'),
          },
          {
            path: ANNUAL_CERTIFICATION_NOT_REQUIRED_PATH,
            name: 'annual-certification-not-required',
            component: () => import('@/pages/annual-certification/not-required/index.vue'),
          },
          {
            path: ANNUAL_CERTIFICATION_COMPLETED_PATH,
            name: 'annual-certification-completed',
            component: () => import('@/pages/annual-certification/completed/index.vue'),
          },
        ],
      },
      {
        path: 'certifications',
        name: 'certifications',
        component: () => import('@/pages/certifications/index.vue'),
        meta: { requiresRoles: [PlatformRole.Compliance] },
      },
      {
        path: 'certifications/:id',
        name: 'certifications-detail',
        component: () => import('@/pages/certifications/[id]/index.vue'),
        meta: { requiresRoles: [PlatformRole.Compliance] },
      },
      {
        path: 'responsibilities',
        name: 'responsibilities',
        component: () => import('@/pages/responsibilities/index.vue'),
      },
      {
        path: 'direct-reports',
        name: 'direct-reports',
        component: () => import('@/pages/direct-reports/index.vue'),
        meta: { requiresRoles: [PlatformRole.LineManager] },
      },
      {
        path: 'line-manager-reviews',
        name: 'line-manager-reviews',
        component: () => import('@/pages/line-manager-reviews/index.vue'),
        meta: { requiresRoles: [PlatformRole.LineManager] },
      },
      {
        // Line Manager Reviews → Annual Process (SMCR-335). Single queue, no
        // sub-tabs, so unlike Compliance's `annual` this has no `children`.
        // Static segments outrank dynamic ones in Vue Router, so this is
        // matched ahead of `line-manager-reviews/:processId` below regardless
        // of declaration order — same static-beats-param note as
        // `compliance-reviews/annual` above.
        path: LINE_MANAGER_ANNUAL_PATH,
        name: 'line-manager-reviews-annual',
        component: () => import('@/pages/line-manager-reviews/annual/index.vue'),
        meta: { requiresRoles: [PlatformRole.LineManager] },
      },
      {
        path: `${LINE_MANAGER_ANNUAL_PATH}/:processId`,
        name: 'line-manager-reviews-annual-detail',
        component: () => import('@/pages/line-manager-reviews/annual/[processId]/index.vue'),
        meta: { requiresRoles: [PlatformRole.LineManager] },
      },
      {
        path: 'line-manager-reviews/:processId',
        name: 'line-manager-review-detail',
        component: () => import('@/pages/line-manager-reviews/[processId]/index.vue'),
        meta: { requiresRoles: [PlatformRole.LineManager] },
      },
      {
        path: 'my-documents',
        name: 'my-documents',
        component: () => import('@/pages/my-documents/index.vue'),
        meta: { requiresRoles: [PlatformRole.Employee] },
      },
      {
        path: 'my-declarations',
        name: 'my-declarations',
        component: () => import('@/pages/my-declarations/index.vue'),
        meta: { requiresRoles: [PlatformRole.Employee] },
      },
      {
        path: 'self-declarations/:processId',
        name: 'self-declaration',
        component: () => import('@/pages/self-declarations/[processId]/index.vue'),
      },
      {
        path: 'annual-self-declarations/:processId',
        name: 'annual-self-declaration',
        component: () => import('@/pages/annual-self-declarations/[processId]/index.vue'),
      },
      {
        path: 'organisation',
        name: 'organisation',
        component: () => import('@/pages/organisation/index.vue'),
        meta: { requiresRoles: [PlatformRole.CompanyAdmin] },
      },
      {
        path: 'organisational-functions',
        name: 'organisational-functions',
        component: () => import('@/pages/organisational-functions/index.vue'),
        meta: { requiresRoles: [PlatformRole.CompanyAdmin] },
      },
      {
        path: 'certification-cycles',
        name: 'certification-cycles',
        component: () => import('@/pages/certification-cycles/index.vue'),
        meta: { requiresRoles: [PlatformRole.CompanyAdmin] },
      },
      {
        path: 'account',
        name: 'account',
        component: () => import('@/pages/account/index.vue'),
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/pages/settings/index.vue'),
        meta: { requiresRoles: [PlatformRole.CompanyAdmin] },
      },
      // Must stay last. A path matching nothing above previously left `matched`
      // empty, so `App.vue`'s `<router-view />` rendered nothing at all — a
      // blank page with no sidebar, escapable only via browser Back. As a child
      // of this layout route it renders a real not-found state with navigation
      // intact. Vue Router ranks this below every static and param route, so it
      // only catches genuine misses.
      {
        path: ':pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/pages/not-found/index.vue'),
      },
    ],
  },
]

function waitForClerk(): Promise<typeof window.Clerk> {
  return new Promise((resolve) => {
    const check = () => {
      if (window.Clerk?.loaded) {
        resolve(window.Clerk)
      } else {
        setTimeout(check, 50)
      }
    }
    check()
  })
}

const SIGNED_OUT_ONLY_ROUTES = new Set(['sign-in', 'sign-up'])
const ALWAYS_PUBLIC_ROUTES = new Set(['invite-accepted'])

export function setupAuthGuard(router: Router) {
  router.beforeEach(async (to) => {
    const clerk = await waitForClerk()

    const isSignedIn = !!clerk?.user
    const name = typeof to.name === 'string' ? to.name : ''
    const isSignedOutOnly = SIGNED_OUT_ONLY_ROUTES.has(name)
    const isPublicRoute = isSignedOutOnly || ALWAYS_PUBLIC_ROUTES.has(name)

    if (!isSignedIn && !isPublicRoute) {
      return { name: 'sign-in', query: { redirect: to.fullPath } }
    }

    if (isSignedIn && isSignedOutOnly) {
      return { name: 'dashboard' }
    }

    const required = to.meta.requiresRoles
    if (!isSignedIn || !required || required.length === 0) return

    let userRoles: PlatformRole[] = []
    try {
      const me = await queryClient.fetchQuery<CurrentUserDto>({
        queryKey: getGetCurrentUserQueryKey(),
        queryFn: ({ signal }) => getCurrentUser(signal),
        retry: false,
      })
      userRoles = me.platformRoles ?? []
    } catch {
      // 404 means there is no linked Person — DefaultLayout swaps to
      // AccountErrorPage. Treat as no roles so the redirect-to-dashboard
      // rule fires and the user lands somewhere that can render the error.
    }

    const allowed = required.some((r) => userRoles.includes(r))
    if (allowed) return

    ToastEventBus.emit('add', {
      severity: 'warn',
      summary: 'Access denied',
      detail: "You don't have permission to view that page.",
      life: 3000,
    })
    return { name: 'dashboard' }
  })
}
